import { executeFetch } from '@unifyapps/network/utils/fetch';
import { requiresSignedFetch } from '@unifyapps/network/utils/fetch/requiresSignedFetch';
import type { UAFetchError } from '@unifyapps/network/utils/fetch/UAFetchError';
import { saveAs } from 'file-saver';
import {
  downloadFileInWebView,
  isFlutterWebView,
  isReactNativeWebView,
} from '../../webview/SDKHandling';
import { getFileNameFromUrl, getServerDeclaredFileName } from './downloadFileName';
import type { SaveAsFunction } from './types';

async function saveFileFromApi(url: string, filename?: string): Promise<void> {
  try {
    // shouldRedirect:false: the response IS a redirect to storage, and without this the logout
    // hook also points the page at it and the file downloads twice
    const response = await executeFetch<Response>(
      { url, method: 'GET' },
      { returnRawResponse: true, shouldRedirect: false },
    );
    // the order a browser download used: it overrode the `download` attribute with the response
    // header, but fell back to that attribute — never to a guess at the url
    const name =
      getServerDeclaredFileName(response) ?? filename ?? getFileNameFromUrl(response.url);
    saveAs(await response.blob(), name);
  } catch (error) {
    console.error(`Failed to download ${url}`, error);
    // a 401 already tripped the logout hook, so the page is leaving and the browser would only
    // repeat the rejected request; every other failure is worth one attempt the old way
    if ((error as UAFetchError | null)?.status === 401) return;
    (saveAs as SaveAsFunction)(url, filename);
  }
}

export const downloadFileInSameTab: SaveAsFunction = (blob, filename) => {
  if (isFlutterWebView() || isReactNativeWebView()) {
    if (typeof blob === 'string') {
      downloadFileInWebView(blob);
    }
    return;
  }

  if (typeof blob === 'string' && requiresSignedFetch(blob)) {
    return saveFileFromApi(blob, filename);
  }

  (saveAs as SaveAsFunction)(blob, filename);
};
